import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  type Firestore,
} from "firebase/firestore";
import { getFirebaseFirestore } from "./firebase";
import {
  DELETE_FIELD,
  type CloudDiffValue,
  type SharedRoomDoc,
  type ShareInvite,
  type SharedInboxEntry,
} from "./sharedRoomTypes";
import { CloudUnavailableError } from "./roomsCloud";

/**
 * Low-level Firestore CRUD for shared multiplayer rooms. Mirrors the
 * structure of {@link import("./roomsCloud")} but for the multi-tenant
 * `sharedRooms/{roomId}` doc-set rather than the per-user
 * `users/{uid}/rooms` collection.
 */

function sharedRoomRef(db: Firestore, roomId: string) {
  return doc(db, "sharedRooms", roomId);
}

/**
 * One-shot fetch of a shared room. Returns `null` when missing or when
 * Firebase isn't configured. Used by the bootstrap path before the
 * realtime listener attaches.
 */
export async function fetchSharedRoom(
  roomId: string
): Promise<SharedRoomDoc | null> {
  const db = getFirebaseFirestore();
  if (!db) return null;
  const snap = await getDoc(sharedRoomRef(db, roomId));
  if (!snap.exists()) return null;
  return snap.data() as SharedRoomDoc;
}

/**
 * Subscribe to realtime updates of a shared room. Throws (via the error
 * callback) when the caller loses access — common cases: the owner
 * revoked, the member was removed, or membership rules deny.
 */
export function subscribeSharedRoom(
  roomId: string,
  cb: (room: SharedRoomDoc | null) => void,
  onError?: (err: Error) => void
): () => void {
  const db = getFirebaseFirestore();
  if (!db) return () => {};
  return onSnapshot(
    sharedRoomRef(db, roomId),
    (snap) => {
      if (!snap.exists()) {
        cb(null);
        return;
      }
      cb(snap.data() as SharedRoomDoc);
    },
    (err) => onError?.(err as Error)
  );
}

/**
 * Apply a granular update to a shared room. The `updates` map uses
 * dotted-path keys (`furniture.f_abc.x`) so two clients touching disjoint
 * items merge without rewriting siblings. Values may be:
 *   - any JSON-serialisable value (passes through),
 *   - the {@link DELETE_FIELD} sentinel (translated to Firestore's
 *     `deleteField()` here so callers don't depend on Firestore types).
 *
 * `updatedAt` is bumped automatically; callers don't have to include it.
 */
export async function applySharedRoomUpdates(
  roomId: string,
  updates: Record<string, CloudDiffValue>
): Promise<void> {
  const db = getFirebaseFirestore();
  if (!db) throw new CloudUnavailableError();
  if (Object.keys(updates).length === 0) return;
  const finalUpdates: Record<string, unknown> = { updatedAt: Date.now() };
  for (const [k, v] of Object.entries(updates)) {
    finalUpdates[k] = v === DELETE_FIELD ? deleteField() : v;
  }
  await updateDoc(sharedRoomRef(db, roomId), finalUpdates);
}

// ---------------------------------------------------------------------------
// Shared inbox (rooms shared WITH me)
// ---------------------------------------------------------------------------

function sharedInbox(db: Firestore, uid: string) {
  return collection(db, "users", uid, "sharedInbox");
}

export function subscribeSharedInbox(
  uid: string,
  cb: (entries: SharedInboxEntry[]) => void,
  onError?: (err: Error) => void
): () => void {
  const db = getFirebaseFirestore();
  if (!db) return () => {};
  return onSnapshot(
    sharedInbox(db, uid),
    (snap) => {
      const entries: SharedInboxEntry[] = [];
      for (const d of snap.docs) {
        const data = d.data() as Partial<SharedInboxEntry>;
        if (
          typeof data.roomId !== "string" ||
          typeof data.ownerUid !== "string" ||
          typeof data.name !== "string"
        ) {
          continue;
        }
        entries.push({
          roomId: data.roomId,
          ownerUid: data.ownerUid,
          ownerDisplayName: data.ownerDisplayName ?? "Someone",
          ownerEmail: data.ownerEmail ?? "",
          name: data.name,
          joinedAt: data.joinedAt ?? 0,
          lastOpenedAt: data.lastOpenedAt ?? 0,
        });
      }
      entries.sort((a, b) => b.joinedAt - a.joinedAt);
      cb(entries);
    },
    (err) => onError?.(err as Error)
  );
}

// ---------------------------------------------------------------------------
// Pending invites (owner-side)
// ---------------------------------------------------------------------------

export async function fetchPendingInvitesForRoom(
  roomId: string,
  ownerUid: string
): Promise<ShareInvite[]> {
  const db = getFirebaseFirestore();
  if (!db) return [];
  const q = query(
    collection(db, "shareInvites"),
    where("roomId", "==", roomId),
    where("ownerUid", "==", ownerUid),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  const invites: ShareInvite[] = [];
  for (const d of snap.docs) {
    const data = d.data() as ShareInvite;
    invites.push({ ...data, id: d.id });
  }
  invites.sort((a, b) => b.createdAt - a.createdAt);
  return invites;
}

export function subscribeInvitesForRoom(
  roomId: string,
  ownerUid: string,
  cb: (invites: ShareInvite[]) => void,
  onError?: (err: Error) => void
): () => void {
  const db = getFirebaseFirestore();
  if (!db) return () => {};
  const q = query(
    collection(db, "shareInvites"),
    where("roomId", "==", roomId),
    where("ownerUid", "==", ownerUid)
  );
  return onSnapshot(
    q,
    (snap) => {
      const invites: ShareInvite[] = [];
      for (const d of snap.docs) {
        const data = d.data() as ShareInvite;
        invites.push({ ...data, id: d.id });
      }
      invites.sort((a, b) => b.createdAt - a.createdAt);
      cb(invites);
    },
    (err) => onError?.(err as Error)
  );
}
